import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Trash2, Shield, LogOut, Loader2, Info } from "lucide-react";
import ChurchBrand from "@/components/ChurchBrand";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
  const [loading, setLoading] = useState(false);
  const [needs, setNeeds] = useState([]);
  const [volunteers, setVolunteers] = useState([]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("admin_token", data.token);
      setToken(data.token);
      setAuthed(true);
      setPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Contraseña incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setAuthed(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [n, v] = await Promise.all([
        api.get("/admin/needs", { headers }),
        api.get("/admin/volunteers", { headers }),
      ]);
      setNeeds(n.data);
      setVolunteers(v.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sesión expirada");
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/needs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Publicación eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const doComplete = async (id) => {
    try {
      await api.post(`/admin/needs/${id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marcada como completada");
      load();
    } catch {
      toast.error("Error");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-8 max-w-md w-full">
          <Link to="/" className="inline-flex items-center gap-2 text-[#8A3A33] hover:underline mb-5 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <ChurchBrand size="md" className="mb-5 text-[#1A1818]" />
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-full bg-[#8A3A33]/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#8A3A33]" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#1A1818]">Acceso Administrador</h1>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Contraseña</Label>
              <Input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-base border-2 border-[#D1C9C1]"
                autoFocus
              />
            </div>
            <Button
              data-testid="admin-login-submit"
              type="submit" disabled={loading}
              className="w-full h-12 text-base font-semibold bg-[#8A3A33] hover:bg-[#6B2B25] text-white"
            >
              {loading ? "Verificando..." : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("es-ES"); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#8A3A33] hover:underline text-base font-medium">
            <ArrowLeft className="w-5 h-5" /> Inicio
          </Link>
          <Button
            data-testid="admin-logout"
            onClick={logout}
            variant="outline"
            className="border-2 border-[#D1C9C1] h-11"
          >
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>

        <ChurchBrand size="md" className="mb-2 text-[#1A1818]" />
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A1818] mb-2">
          Panel de Administración
        </h1>
        <p className="text-base text-[#4A4646] mb-6">
          Gestione publicaciones y vea las respuestas de voluntarios.
        </p>

        <div className="bg-[#FEF3C7] border-2 border-[#D97706]/30 rounded-lg p-4 mb-6 flex gap-3">
          <Info className="w-5 h-5 text-[#92400E] shrink-0 mt-0.5" />
          <div className="text-sm text-[#92400E] space-y-1">
            <p><strong>Instrucciones:</strong></p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Las publicaciones se archivan automáticamente al completarse.</li>
              <li>Las publicaciones con más de 60 días se eliminan automáticamente.</li>
              <li>Puede eliminar o marcar como completada cualquier publicación desde aquí.</li>
            </ul>
          </div>
        </div>

        <Tabs defaultValue="needs">
          <TabsList className="grid w-full grid-cols-2 h-12 bg-white border-2 border-[#D1C9C1]">
            <TabsTrigger data-testid="tab-needs" value="needs" className="text-base">
              Necesidades ({needs.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-volunteers" value="volunteers" className="text-base">
              Voluntarios ({volunteers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="needs" className="mt-5">
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin text-[#8A3A33] mx-auto my-10" />
            ) : needs.length === 0 ? (
              <p className="text-center text-[#4A4646] py-10">Sin publicaciones.</p>
            ) : (
              <div className="space-y-3">
                {needs.map((n) => (
                  <div key={n.id} data-testid={`admin-need-${n.id}`} className="bg-white border-2 border-[#D1C9C1] rounded-xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-[#8A3A33]/10 text-[#8A3A33]">{n.category}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-[#F3F1EF] text-[#4A4646]">{n.status}</span>
                          {n.archived && <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-[#FEF3C7] text-[#92400E]">Archivada</span>}
                        </div>
                        <h3 className="font-serif text-lg font-bold text-[#1A1818]">{n.title}</h3>
                        <p className="text-sm text-[#4A4646] mt-1">{n.description}</p>
                        <p className="text-xs text-[#4A4646] mt-2">
                          Por <strong>{n.name}</strong> · {formatDate(n.created_at)}
                          {n.volunteers?.length > 0 && ` · ${n.volunteers.length} voluntario(s)`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {!n.archived && (
                          <Button
                            data-testid={`admin-complete-${n.id}`}
                            size="sm" onClick={() => doComplete(n.id)}
                            className="bg-[#2C5545] hover:bg-[#1F3D32] text-white"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Completar
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`admin-delete-${n.id}`} size="sm" variant="outline" className="border-2 border-red-300 text-red-700 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
                              <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => doDelete(n.id)} className="bg-red-700 hover:bg-red-800" data-testid={`admin-confirm-delete-${n.id}`}>
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="volunteers" className="mt-5">
            {volunteers.length === 0 ? (
              <p className="text-center text-[#4A4646] py-10">Aún no hay voluntarios.</p>
            ) : (
              <div className="space-y-3">
                {volunteers.map((v, i) => (
                  <div key={i} data-testid={`admin-volunteer-${i}`} className="bg-white border-2 border-[#D1C9C1] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-[#1A1818] text-base">{v.volunteer_name}</p>
                        <p className="text-sm text-[#4A4646]">{v.volunteer_contact}</p>
                        <p className="text-xs text-[#4A4646] mt-1">Se ofreció: {formatDate(v.offered_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-[#1A1818]">Para: <em>{v.need_title}</em></p>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase bg-[#8A3A33]/10 text-[#8A3A33]">{v.need_category}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
